import React, { useEffect, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import { saveUserSettings, SETTING_KEYS } from '../utils/userSettings';
import TrackModePlaybackControls from '../components/TrackModePlaybackControls';
import {
  markTrackModeAppliedLocal,
  buildTrackModeSettingsPayload,
  validatePlaybackRange,
  listOnlineMembersForToolbar,
  buildResetDashboardFiltersPatch,
  clearTrackModeLocalStorage,
  requestReplayEarthAnimation,
  pickFirstPlaybackTrack,
  pickPlaybackTracksByNames,
  startTrackPlayback,
  stopTrackPlayback,
  clearPlaybackCursorMarkers,
  syncPlaybackCursorMarkers,
  locateCommanderOnMap,
  flyToMemberOnMap,
  buildLastDeviceLocationRecord,
  writeLastDeviceLocationToStorage
} from '../utils/dashboard';

/**
 * 指挥页工具栏 / 轨迹模式 / 回放 / 定位相关动作
 */
export default function useDashboardToolbarActions({
  getMap,
  members,
  membersRef,
  trackMode,
  trackMinutes,
  trackHours,
  trackDays,
  playbackRange,
  isPlaying,
  setIsPlaying,
  setPlaybackIndex,
  setTrackModeApplied,
  setStartTime,
  setEndTime,
  setHideInvalid,
  setShowOnlineOnly,
  setTrackMode,
  setTrackMinutes,
  setTrackHours,
  setTrackDays,
  setPlaybackRange,
  setRefreshInterval,
  showTracks,
  toggleTrack,
  startRealTimeTrackDrawing,
  fetchDevices,
  fetchTracks,
  trackData,
  playbackTimerRef,
  isAnimationActiveRef,
  shouldUseEarthOverviewRef,
  stopEarthAnimation,
  onCommanderLocated,
  /** () => 当前底图 id，定位/飞行纠偏用 */
  getBasemapId,
  playbackTargetNames = [],
  setPlaybackTargetNames,
  playbackSpeed = 10,
  setPlaybackSpeed,
  playbackIndex = 0,
  trackColors = {},
  playbackMarkersRef = null
}) {
  const [playbackSessionActive, setPlaybackSessionActive] = useState(false);
  const playbackSpeedRef = useRef(playbackSpeed);
  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
    // 仅滑条变化时重排；开播不走这里，避免打断首帧间隔
    if (!isPlaying) return;
    if (typeof playbackTimerRef?.__zhxtPlaybackReschedule === 'function') {
      try { playbackTimerRef.__zhxtPlaybackReschedule(); } catch (_) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只要速度变就读最新 isPlaying/timer
  }, [playbackSpeed]);

  const applyTrackModeSettings = () => {
    setTrackModeApplied(true);
    console.log('应用轨迹模式时间筛选，模式:', trackMode);
    markTrackModeAppliedLocal(trackMode, trackMinutes, trackHours, trackDays);
    const trackModeSettings = buildTrackModeSettingsPayload(
      trackMode, trackMinutes, trackHours, trackDays, SETTING_KEYS
    );
    saveUserSettings(trackModeSettings).then((success) => {
      if (success) console.log('轨迹模式设置已保存到数据库');
      else console.warn('轨迹模式设置保存到数据库失败');
    });
    fetchDevices();
    fetchTracks();
    message.success('轨迹模式设置已应用，数据已刷新');
  };

  const startPlayback = () => {
    if (isPlaying) return;
    const names = Array.isArray(playbackTargetNames) ? playbackTargetNames.filter(Boolean) : [];
    // 多选优先；未选时回退到第一条有数据的轨迹
    let pickedList = names.length
      ? pickPlaybackTracksByNames(trackData, names)
      : [];
    if (!pickedList.length) {
      const one = pickFirstPlaybackTrack(trackData);
      if (one) pickedList = [one];
    }
    if (!pickedList.length) {
      message.warning('没有可回放的轨迹，请先应用轨迹模式并勾选目标');
      return;
    }
    // 打开所选轨迹显示
    pickedList.forEach(({ key }) => {
      if (key && toggleTrack && !showTracks?.[key]) toggleTrack(key);
    });
    // 主轨：点数最多
    const picked = pickedList.reduce((a, b) => (b.track.length > a.track.length ? b : a), pickedList[0]);
    setIsPlaying(true);
    setPlaybackSessionActive(true);
    // 已在末尾（播完或拖到终点）再点播放 → 从头；暂停中途 → 从 playbackIndex 续播
    let from = Math.min(Math.max(0, Number(playbackIndex) || 0), picked.track.length - 1);
    if (picked.track.length > 1 && from >= picked.track.length - 1) {
      from = 0;
      setPlaybackIndex(0);
    }
    const map = getMap?.();
    const cursorNames = pickedList.map((x) => x.key).filter(Boolean);
    const dataForCursors = {};
    pickedList.forEach((x) => {
      if (x?.key) dataForCursors[x.key] = x.track;
    });
    startTrackPlayback({
      track: picked.track,
      map,
      timerRef: playbackTimerRef,
      startIndex: from,
      speed: playbackSpeed,
      getSpeed: () => playbackSpeedRef.current,
      centerTracks: pickedList.map((x) => x.track),
      onIndex: setPlaybackIndex,
      onFinished: () => {
        setIsPlaying(false);
        setPlaybackSessionActive(false);
      },
      shouldContinue: () => true,
      playbackMarkersRef,
      trackData: dataForCursors,
      playbackNames: cursorNames,
      primaryName: picked.key,
      trackColors,
      members: membersRef?.current || members || []
    });
  };

  const paintPlaybackCursorsAt = (idx) => {
    const map = getMap?.();
    if (!map || !playbackMarkersRef) return;
    const names = Array.isArray(playbackTargetNames) ? playbackTargetNames.filter(Boolean) : [];
    let pickedList = names.length ? pickPlaybackTracksByNames(trackData, names) : [];
    if (!pickedList.length) {
      const first = pickFirstPlaybackTrack(trackData);
      if (first) pickedList = [first];
    }
    if (!pickedList.length) return;
    const dataForCursors = {};
    pickedList.forEach((x) => {
      if (x?.key) dataForCursors[x.key] = x.track;
    });
    const primary = pickedList.reduce((a, b) => (b.track.length > a.track.length ? b : a), pickedList[0]);
    syncPlaybackCursorMarkers(map, {
      trackData: dataForCursors,
      names: pickedList.map((x) => x.key).filter(Boolean),
      primaryName: primary?.key || null,
      index: idx,
      trackColors,
      members: membersRef?.current || members || [],
      markersRef: playbackMarkersRef
    });
  };

  const handlePlaybackIndexChange = (idx) => {
    setPlaybackIndex(idx);
    // 拖动进度条时也同步游标（未播放状态）
    if (!isPlaying) paintPlaybackCursorsAt(idx);
  };

  /** 暂停：停表、保留进度；下次播放从该点续播并飞到暂停位置 */
  const pausePlayback = () => {
    setIsPlaying(false);
    stopTrackPlayback(playbackTimerRef);
    paintPlaybackCursorsAt(playbackIndex);
  };

  /** 停止回放：进度归零；下次播放从头居中开播 */
  const stopPlayback = () => {
    setIsPlaying(false);
    setPlaybackSessionActive(false);
    stopTrackPlayback(playbackTimerRef);
    setPlaybackIndex(0);
    paintPlaybackCursorsAt(0);
  };

  const handleStartPlaybackClick = () => {
    const names = Array.isArray(playbackTargetNames) ? playbackTargetNames.filter(Boolean) : [];
    const hasTrack = names.some((n) => (trackData?.[n] || []).length > 0)
      || Object.keys(trackData || {}).some((k) => (trackData[k] || []).length > 0);
    // 已有轨迹数据时可直接回放；否则仍要求选择起止时间（配合轨迹模式拉取）
    if (!hasTrack) {
      const checked = validatePlaybackRange(playbackRange);
      if (!checked.ok) {
        message.warning(checked.message);
        return;
      }
    }
    startPlayback();
  };

  const locateCommander = () => {
    locateCommanderOnMap({
      getMap,
      basemapId: getBasemapId?.(),
      beforeFly: () => {
        // 必须先停自转，否则 jumpTo 会打断 flyTo，变成「先偏一下再瞬移」
        if (isAnimationActiveRef?.current) {
          stopEarthAnimation?.();
          if (shouldUseEarthOverviewRef) shouldUseEarthOverviewRef.current = false;
        }
      },
      username: typeof localStorage !== 'undefined' ? localStorage.getItem('username') || '' : '',
      onLocated: (record) => {
        onCommanderLocated?.(record);
      },
      ui: {
        loading: (msg) => message.loading(msg, 0),
        destroy: () => message.destroy(),
        success: (msg) => message.success(msg),
        error: (msg) => message.error(msg)
      },
      duration: 2800
    });
  };

  const handleResetDashboardConfig = () => {
    const patch = buildResetDashboardFiltersPatch();
    setStartTime(patch.startTime);
    setEndTime(patch.endTime);
    setHideInvalid(patch.hideInvalid);
    setShowOnlineOnly(patch.showOnlineOnly);
    setTrackMode(patch.trackMode);
    setTrackMinutes(patch.trackMinutes);
    setTrackHours(patch.trackHours);
    setTrackDays(patch.trackDays);
    setPlaybackRange(patch.playbackRange);
    setIsPlaying(patch.isPlaying);
    setPlaybackIndex(patch.playbackIndex);
    setTrackModeApplied(patch.trackModeApplied);
    clearTrackModeLocalStorage();
    clearPlaybackCursorMarkers(playbackMarkersRef);
    setPlaybackSessionActive(false);
    fetchDevices();
    message.success('配置已重置，数据已刷新');
  };

  const handleReplayEarth = () => {
    if (requestReplayEarthAnimation()) {
      message.info('已设置重新显示地球动画，即将刷新页面');
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  const handleShowOnlineTracks = () => {
    const onlineMembers = listOnlineMembersForToolbar(membersRef.current || members);
    if (onlineMembers.length === 0) {
      message.warning('当前没有在线队员');
      return;
    }
    onlineMembers.forEach((member) => {
      if (!showTracks[member.name]) toggleTrack(member.name);
    });
    message.success(`已为 ${onlineMembers.length} 个在线队员显示轨迹`);
  };

  const handleDrawOnlineTracks = () => {
    const onlineMembers = listOnlineMembersForToolbar(membersRef.current || members);
    if (onlineMembers.length === 0) {
      message.warning('当前没有在线队员');
      return;
    }
    onlineMembers.forEach((member) => startRealTimeTrackDrawing(member.name, { quiet: true }));
    message.success(`已为 ${onlineMembers.length} 个在线队员开启实时轨迹绘制`);
  };

  const handleRefreshIntervalChange = (newInterval) => {
    setRefreshInterval(newInterval);
    saveUserSettings({ [SETTING_KEYS.REFRESH_INTERVAL]: newInterval });
    message.success(`刷新间隔已设置为 ${newInterval / 1000} 秒`);
  };

  const flyToMember = (member) => {
    const map = getMap?.();
    if (!map) {
      console.log('无法飞行到队员位置：地图未初始化');
      message.warning('地图尚未就绪，请稍后再试');
      return;
    }
    const ok = flyToMemberOnMap(map, member, {
      heightMeters: 600,
      duration: 2800,
      speed: 1.2,
      curve: 1.42,
      basemapId: getBasemapId?.(),
      beforeFly: () => {
        if (isAnimationActiveRef?.current) {
          stopEarthAnimation?.();
          if (shouldUseEarthOverviewRef) shouldUseEarthOverviewRef.current = false;
        }
      }
    });
    if (!ok) {
      console.log('无法飞行到队员位置：队员坐标无效', member);
      message.warning(`${member?.name || '该队员'} 暂无有效坐标，无法定位`);
      return;
    }
    // 记住本次设备定位，刷新后地球空白点击可飞回
    const deviceRecord = buildLastDeviceLocationRecord(member);
    if (deviceRecord) {
      writeLastDeviceLocationToStorage(deviceRecord);
    }
    console.log(`飞行到队员 ${member.name} 位置:`, {
      center: [Number(member.last_longitude), Number(member.last_latitude)],
      currentZoom: map.getZoom()
    });
  };

  const playbackControlProps = {
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    playbackRange,
    isPlaying,
    onTrackModeChange: setTrackMode,
    onTrackMinutesChange: setTrackMinutes,
    onTrackHoursChange: setTrackHours,
    onTrackDaysChange: setTrackDays,
    onPlaybackRangeChange: setPlaybackRange,
    onApplyTrackMode: applyTrackModeSettings,
    onStartPlayback: handleStartPlaybackClick,
    onPausePlayback: pausePlayback,
    onStopPlayback: stopPlayback,
    playbackSessionActive,
    members,
    trackData,
    trackColors,
    playbackTargetNames,
    onPlaybackTargetNamesChange: setPlaybackTargetNames,
    playbackSpeed,
    onPlaybackSpeedChange: setPlaybackSpeed,
    playbackIndex,
    onPlaybackIndexChange: handlePlaybackIndexChange,
    getMap,
    showTracks,
    onToggleTrack: toggleTrack
  };

  const trackModeAndPlaybackUI = (
    <TrackModePlaybackControls {...playbackControlProps} variant="compact" />
  );

  const playbackPanelUI = (
    <TrackModePlaybackControls {...playbackControlProps} variant="full" />
  );

  return {
    applyTrackModeSettings,
    startPlayback,
    pausePlayback,
    stopPlayback,
    handleStartPlaybackClick,
    locateCommander,
    handleResetDashboardConfig,
    handleReplayEarth,
    handleShowOnlineTracks,
    handleDrawOnlineTracks,
    handleRefreshIntervalChange,
    flyToMember,
    trackModeAndPlaybackUI,
    playbackPanelUI
  };
}
